import { motion } from "framer-motion";
import { ReactNode, forwardRef } from "react";

const variants = {
  initial: { opacity: 0, y: 12, scale: 0.97 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: {
    opacity: 0,
    scale: 0.97,
    transition: { duration: 0.15, ease: "easeOut" },
  },
};

const transition = {
  type: "spring",
  stiffness: 260,
  damping: 30,
  mass: 0.5,
};

const AnimatedElement = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    className?: string;
    isEnabled?: boolean;
    style?: React.CSSProperties;
    isDragging?: boolean;
  }
>(({ children, className, isEnabled = true, style, isDragging = false }, ref) => {
  // Disable animations during drag to prevent conflicts with @hello-pangea/dnd
  if (!isEnabled || isDragging) {
    return (
      <div ref={ref} className={className} style={style}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      layout={!isDragging}
      initial="initial"
      animate="animate"
      exit="exit"
      variants={variants}
      transition={{
        ...transition,
        // Smoother layout animations for better performance
        layout: {
          type: "spring",
          stiffness: 200,
          damping: 25,
          mass: 0.6,
        },
      }}
      className={className}
      style={{
        ...style,
        // Ensure proper stacking context for drag operations
        position: style?.position || "relative",
        // Optimize for GPU acceleration
        willChange: "transform, opacity",
      }}
    >
      {children}
    </motion.div>
  );
});

AnimatedElement.displayName = "AnimatedElement";

export default AnimatedElement;
